/* eslint-disable max-len */
import COS from '@/plugins/cos_sdk/src/cos';
import eventProxy from '@/scripts/tools/eventProxy';
import user from '@/scripts/helper/user';
import { queryParamsToString } from '@/scripts/tools/utils';
import { getRequestInfo } from '@/scripts/tools/cosSDKUtils';
import { store } from '@/scripts/store';
import storage, { Storage } from '@/scripts/tools/storage';

const cos = new COS({
  // 可选参数
  FileParallelLimit: 2, // 控制文件上传并发数
  ChunkParallelLimit: 2, // 控制单个文件下分片上传并发数
  ChunkSize: 1024 * 1024, // 控制分片大小，单位 B
  ProgressInterval: 1000, // 控制 onProgress 回调的间隔
  SliceSize: 8 * 1024 * 1024,

  UploadCheckContentMd5: true,
  CopyChunkParallelLimit: 1, // 复制文件的分块并发数
  UserAgent: window.navigator.userAgent,
  getAuthorization(options, callback) {
    user.getCredential().then(credential => {
      const SecretId = credential.SecretId || credential.tmpSecretId;
      const SecretKey = credential.SecretKey || credential.tmpSecretKey;
      options = { ...options, SecretId, SecretKey };
      const res = { Authorization: COS.getAuthorization(options) };
      if (credential.token) {
        res.SecurityToken = credential.token;
      }
      callback(res);
    });
  },
});

// 可配置的字段
[
  'UploadDoubleCheck',
].forEach(key => {
  cos.options[key] = storage.get(`upload.${key}`);
  Storage.onChange(`upload.${key}`, val => {
    cos.options[key] = val;
  });
});

cos.on('list-update', data => {
  eventProxy.emit('upload-list-update', data);
});

cos.on('before-send', opt => {
  const res = getRequestInfo(opt);

  if (res.action === 'PutObject' || res.action === 'UploadPart') {
    // opt.qs['x-cos-channel-id'] = 'cosbrowser_web';
    // 流量大的接口直接走跨域请求
    return;
  } if (res.action === 'GetService') {
    return;
  }

  if (opt.qs) {
    const qsStr = queryParamsToString(opt.qs);
    if (qsStr) {
      opt.url += (opt.url.indexOf('?') > -1 ? '&' : '?') + qsStr;
    }
    delete opt.qs;
  }

  // 避免 url 里的特殊字符被转义
  opt.url = opt.url.replace(/^(https?:\/\/(.*)\.(cos|ci|pic)\.([^.]+)\.myqcloud\.com)\/(.*)$/, (o, host, bucket, hostType, region, pathQuery) => `${store.cosbrowserServerUrl}/cosproxy/?bucket=${bucket}&region=${region}&hostType=${hostType}&params=${encodeURIComponent(pathQuery)}`);
});

// eventProxy.on('after-logout', () => {
//   cos.removeAllTask();
// });

// 基于cos.getObjectUrl修改，设置桶里面的文件和文件夹的下载url
cos.getObjectUrlByDomain = (params, callback) => {
  // 拿到当前桶的下载域名，如果不是默认的域名，那么就是自定义域名，只能使用http协议，并且需要将原本默认域名替换,且不支持后缀式
  let bucketDownloadDomain = '';
  if (store.downloadDomain[params.Bucket]) {
    bucketDownloadDomain = store.downloadDomain[params.Bucket];
  } else {
    bucketDownloadDomain = storage.get(`downloadDomain.${params.Bucket}`);
  }
  let protocol = params.Protocol || cos.options.Protocol;
  let domain = cos.options.Domain;
  let { ForcePathStyle } = cos.options;
  if (bucketDownloadDomain !== 'cosDefaultDomain' && bucketDownloadDomain !== '') {
    protocol = 'http:';
    domain = bucketDownloadDomain;
    ForcePathStyle = false;
  }
  params.protocol = protocol;
  params.domain = domain;
  params.ForcePathStyle = ForcePathStyle;
  const url = cos.getObjectUrl(params, callback);
  return url;
};

// 生成存储桶访问域名
cos.getDefaultBucketDomain = params => {
  let url = cos.getObjectUrl({
    Bucket: params.Bucket,
    Region: params.Region,
    Key: '/',
    Sign: false,
  });
  url = (url || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  return url;
};

// 获取文件下载链接
/**
 *
 * @param {*} url 对象的公有链接
 * @param {*} unSigned 是否签名: true-不签名 false-签名
 * @param {*} expires 有效时间
 * @param {*} method 方法
 * @param {*} force 是否强制获取一边密钥（oauth 需要）
 */
cos.getDownloadUrl = async function getDownloadUrl(url, unSigned, expires, method, force) {
  url = url.replace(/\?.*$/, '');
  const hostSign = url.replace(/^https?:\/\/(.*)\.cos\.([^.]+)\.myqcloud\.com\/(.*)$/, '$1.cos.$2.myqcloud.com');
  const key = decodeURIComponent(url.replace(/^\w+:\/\/[^/]+/, '')) || '/';
  if (unSigned) return url;
  const credential = await user.getCredential(undefined, force);
  const SecretId = credential.SecretId || credential.tmpSecretId;
  const SecretKey = credential.SecretKey || credential.tmpSecretKey;
  const authOpt = {
    SecretId,
    SecretKey,
    Method: method || 'GET',
    Key: key,
    Expires: parseInt(expires, 10) || 1800,
    SystemClockOffset: this.options.SystemClockOffset,
    Headers: {
      host: hostSign,
    },
  };
  let result = `${url}?${cos.getAuth(authOpt)}`;
  if (credential.token) {
    result += `&x-cos-security-token=${credential.token}`;
  }
  return result;
};

// 获取完整的文件列表
cos.listFiles = function listFiles(params, callback) {
  const Contents = [];
  let marker;
  const next = function next() {
    params.Marker = marker;
    cos.getBucket(params, (err, data) => {
      if (err) {
        err.errorAction = 'getBucket';
        callback(err);
      } else {
        if (data && data.Contents && data.Contents.length) {
          Contents.push(...data.Contents);
        }
        if (data.IsTruncated === 'true') {
          marker = data.NextMarker;
          next();
        } else {
          callback(null, { Contents });
        }
      }
    });
  };
  next();
};

// 覆盖检查任务状态的方法，协助文件同步的任务状态判断
const syncUploading = {};
cos.__isRunningTask = cos._isRunningTask;
cos._isRunningTask = id => syncUploading[id] || cos.__isRunningTask(id);
cos.setSyncUploading = id => {
  syncUploading[id] = true;
};
cos.removeSyncUploading = id => {
  delete syncUploading[id];
};
cos.removeTask = cos.cancelTask;

export default cos;
